import React from "react";
import SafeIcon from "../../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiEye, FiTrendingDown } = FiIcons;

function money(value, currency) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(amount);
  }
}

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ClientExpensesTable({
  expenses,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onView,
}) {
  const pages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-violet-100 border-t-violet-600" />
        <p className="mt-4 text-sm text-slate-600">Loading expenses…</p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[850px] border-collapse text-left">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4">Description</th>
              <th className="px-5 py-4">Vendor / Payee</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4 text-right">Amount</th>
              <th className="px-5 py-4">Source</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {expenses.map((expense) => (
              <tr key={expense.id} onClick={() => onView(expense)} tabIndex={0} onKeyDown={(event) => event.key === "Enter" && onView(expense)} className="cursor-pointer text-sm outline-none hover:bg-slate-50 focus:bg-violet-50/50">
                <td className="whitespace-nowrap px-5 py-4 text-slate-600">{dateLabel(expense.date)}</td>
                <td className="max-w-[280px] px-5 py-4 font-semibold text-slate-800"><span className="block truncate">{expense.description || "No description"}</span></td>
                <td className="px-5 py-4 text-slate-600">{expense.vendorName || "—"}</td>
                <td className="px-5 py-4 text-slate-700">{expense.account?.name || "Category unavailable"}</td>
                <td className="whitespace-nowrap px-5 py-4 text-right font-semibold tabular-nums text-slate-900">{money(expense.amount, expense.currency)}</td>
                <td className="px-5 py-4 text-slate-500">{expense.source || "—"}</td>
                <td className="px-5 py-4 text-right">
                  <button type="button" onClick={(event) => { event.stopPropagation(); onView(expense); }} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-bold text-violet-700 hover:bg-violet-50">
                    <SafeIcon icon={FiEye} /> View
                  </button>
                </td>
              </tr>
            ))}
            {!expenses.length && (
              <tr><td colSpan="7" className="px-6 py-14 text-center">
                <SafeIcon icon={FiTrendingDown} className="mx-auto text-3xl text-slate-300" />
                <p className="mt-3 text-sm font-bold text-slate-800">No expenses match these filters</p>
                <p className="mt-1 text-sm text-slate-500">Try a different search or date range.</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <footer className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">{totalCount ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}` : "0 expenses"}</p>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs disabled:opacity-40">Previous</button>
          <span className="px-2 py-2 text-xs text-slate-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs disabled:opacity-40">Next</button>
        </div>
      </footer>
    </section>
  );
}